import { extendObservable, action, toJS } from 'mobx'
import uniq from 'lodash/uniq'

import { emailTemplateApi } from '@atheneum/api'
import { employeeApi } from '@atheneum/api'

import yup from 'yup'
import { showDialog } from '@components/Dialog'
import { masterTemplateStatic } from '../../../api/emailTemplate/helpers/masterTemplate.static'

class EmailTemplates {
  constructor(staticData) {
    extendObservable(this, {
      staticData,
      loading: null,
      loadingTemplateOptions: null,
      activeTemplate: null,
      loadingTemplateData: null,
      loadingSenders: false,
      error: null,
      touched: [],
      validationErrors: null,
      template: this.getEmptyTemplate(),
      templates: [],
      templateOptions: [],
      templateTypeOptions: masterTemplateStatic.templateTypes,
      senders: []
    })
  }

  static validationSchema = yup.object().shape({
    name: yup
      .string()
      .required('Name field is required')
      .typeError('Name field is required'),
    group: yup
      .string()
      .required('Group field is required')
      .typeError('Group field is required'),
    subject: yup
      .string()
      .required('Subject field is required')
      .typeError('Subject field is required'),
    content: yup
      .string()
      .required('Content field is required')
      .typeError('Content field is required')
  })

  static newTemplateValues = {
    id: null,
    name: '',
    subject: '',
    content: ''
  }

  setValue = (key, val) => {
    this.activeTemplate = { ...this.activeTemplate, [key]: val }
    if (key === 'group') {
      this.activeTemplate.placeholders = this.templates.find(
        i => i.group === val
      )?.placeholders
    }
    this.touched = uniq([...this.touched, key])
    this.validate()
  }

  saveActive = action(async () => {
    this.touched = ['name', 'subject', 'content', 'group']
    if (!this.validate()) throw new Error('validation issue')
    if (this.activeTemplate.id) {
      const template = await this.update(this.activeTemplate.id, {
        subject: this.activeTemplate.subject,
        content: this.activeTemplate.content
      })
      this.setActiveTemplate(template)
    } else {
      const template = await this.create({
        subject: this.activeTemplate.subject,
        name: this.activeTemplate.name,
        group: this.activeTemplate.group,
        content: this.activeTemplate.content,
        placeholders: this.activeTemplate.placeholders
      })
      this.setActiveTemplate(template)
    }
    await this.getAll()
  })

  validate = () => {
    try {
      const res = EmailTemplates.validationSchema.validateSync(
        this.activeTemplate,
        {
          abortEarly: false
        }
      )
      this.validationErrors = null
      return res
    } catch (err) {
      this.validationErrors = err
      return false
    }
  }

  setActiveTemplate = template => {
    this.activeTemplate = { ...template }
    this.touched = []
    this.validate()
  }

  getEmptyTemplate = () => {
    return {
      name: null,
      subject: null,
      content: null,
      placeholders: null,
      to: [],
      cc: [],
      attachments: []
    }
  }

  getAll = () => {
    this.loading = true

    return emailTemplateApi
      .getAll()
      .then(templates => {
        this.templates.replace(templates)
        this.loading = false
        return templates
      })
      .catch(err => {
        this.error = err
        this.loading = false
        return Promise.reject(err)
      })
  }

  addTemplate = async () => {
    const template = await emailTemplateApi.create()
    this.templates.push(template)
    return template
  }

  create = async data => {
    const template = await emailTemplateApi.create(data)
    return template
  }

  update = (templateId, template) => {
    this.loading = true

    return emailTemplateApi
      .update(templateId, template)
      .then(template => {
        this.template = template
        this.loading = false
        return template
      })
      .catch(err => {
        this.error = err
        this.loading = false
        return Promise.reject(err)
      })
  }

  getTemplateOptions = group => {
    this.template = this.getEmptyTemplate()
    this.loadingTemplateOptions = true
    return emailTemplateApi
      .getByGroup(group)
      .then(options => {
        this.templateOptions.replace(options)
        this.loadingTemplateOptions = false
        return options
      })
      .catch(err => {
        this.error = err
        this.loadingTemplateOptions = false
        return Promise.reject(err)
      })
  }

  getTemplateData = (url, templateName, params) => {
    this.loadingTemplateData = true

    url = toJS(url)

    const urls = Array.isArray(url) ? url : [url]

    const promises = urls.map(u =>
      emailTemplateApi.getTemplateData(u, templateName, params)
    )

    return Promise.all(promises)
      .then(responses => {
        const template = responses[0].template
        const recipients = responses.reduce(
          (acc, response) => {
            acc.cc.push(response.recipients.cc)
            acc.to.push(response.recipients.to)
            return acc
          },
          {
            cc: [],
            to: []
          }
        )

        recipients.cc = recipients.cc.reduce((prev, next) => prev.concat(next))
        recipients.to = recipients.to.reduce((prev, next) => prev.concat(next))

        this.template = { ...template, ...recipients }
        this.loadingTemplateData = false
        return { ...template, ...recipients }
      })
      .catch(err => {
        this.loadingTemplateData = false
        return showDialog({
          title: 'Error',
          text: err.message,
          type: 'error'
        }).then(() => Promise.reject(err))
      })
  }

  getSenders = senderIds => {
    this.loadingSenders = true
    employeeApi.getMultiple(senderIds).then(senders => {
      this.senders.replace(
        senders.map(s => ({
          label: s.fullName,
          id: s.id
        }))
      )
      this.loadingSenders = false
    })
  }

  sendTemplate = (url, template) => {
    return emailTemplateApi.sendTemplate(url, template).catch(err => {
      this.error = err
      return Promise.reject(err)
    })
  }
}

export default EmailTemplates
